/**
 * Decision Renderer - Deterministic textual explanations for priority decisions.
 * 
 * Provides stable, deterministic text rendering of:
 * - Executive summaries for snapshots
 * - Item-level rationales
 * - Snapshot deltas
 * 
 * All rendering is deterministic - same inputs always produce same outputs.
 * No randomness, no AI - pure template-based rendering.
 */

import type { PrioritySnapshot } from "./priorityTruth";
import type {
  ExecutivePriorityView,
  TracePriorityView,
} from "../arrange/explainability";
import type { Item } from "../harmonizer/types";

/**
 * Renders an executive summary for a priority snapshot.
 * 
 * Provides a high-level, human-readable summary of the snapshot's decisions.
 * 
 * @param view - Executive priority view for the snapshot
 * @param snapshot - The priority snapshot
 * @param _totalItems - Total number of items in the snapshot (currently unused by the renderer)
 * @returns Deterministic executive summary text
 */
export function renderExecutiveSummary(
  view: ExecutivePriorityView,
  snapshot: PrioritySnapshot,
  _totalItems: number
): string {
  const toolName = snapshot.toolName;
  const itemCount = snapshot.scoreSet.scores.length;
  const topRanked = snapshot.scoreSet.scores
    .filter((s) => s.rank !== undefined && s.rank <= 3)
    .sort((a, b) => (a.rank || 0) - (b.rank || 0))
    .slice(0, 3);

  let summary = `Priority decisions using ${toolName}\n\n`;
  summary += `Evaluated ${itemCount} item${itemCount !== 1 ? "s" : ""} and produced a ranked priority list.\n\n`;

  if (view.rank !== null) {
    summary += `This item is ranked #${view.rank} of ${itemCount}.\n`;
  }

  if (view.numericScore !== null) {
    summary += `Score: ${view.numericScore.toFixed(2)}\n`;
  }

  if (view.categoryId) {
    summary += `Category: ${view.categoryId}\n`;
  }

  if (view.keyDrivers.length > 0) {
    summary += `\nKey factors: ${view.keyDrivers.join(", ")}\n`;
  }

  if (topRanked.length > 0) {
    summary += `\nTop priorities:\n`;
    topRanked.forEach((score, idx) => {
      const rank = score.rank || idx + 1;
      const scoreText =
        score.numericScore !== undefined
          ? ` (score: ${score.numericScore.toFixed(2)})`
          : "";
      summary += `  ${rank}. Item ${score.itemId}${scoreText}\n`;
    });
  }

  return summary;
}

/**
 * Renders item-level rationales for all items in a snapshot.
 * 
 * @param trace - Trace priority view for the snapshot
 * @param items - Array of items (for labels)
 * @returns Map of itemId -> rationale text
 */
export function renderItemRationales(
  trace: TracePriorityView,
  items: Item[]
): Record<string, string> {
  const rationales: Record<string, string> = {};
  const itemMap = new Map(items.map((item) => [item.id, item]));

  const explanation = trace.rawExplanation;
  const item = itemMap.get(trace.itemId);
  const itemLabel = item?.label || trace.itemId;

  let rationale = `Rationale for "${itemLabel}":\n\n`;

  rationale += `Tool: ${explanation.toolUsed}\n`;

  if (explanation.finalRank !== null) {
    rationale += `Final Rank: #${explanation.finalRank}\n`;
  }

  if (explanation.finalScore !== null) {
    rationale += `Final Score: ${explanation.finalScore.toFixed(2)}\n`;
  }

  if (explanation.finalCategory) {
    rationale += `Category: ${explanation.finalCategory}\n`;
  }

  if (Object.keys(explanation.toolConfig).length > 0) {
    rationale += `\nTool Configuration:\n`;
    for (const [key, value] of Object.entries(explanation.toolConfig)) {
      rationale += `  ${key}: ${JSON.stringify(value)}\n`;
    }
  }

  if (explanation.rawInputs && Object.keys(explanation.rawInputs).length > 0) {
    rationale += `\nInput Values:\n`;
    for (const [key, value] of Object.entries(explanation.rawInputs)) {
      if (typeof value === "number") {
        rationale += `  ${key}: ${value.toFixed(2)}\n`;
      } else {
        rationale += `  ${key}: ${JSON.stringify(value)}\n`;
      }
    }
  }

  if (explanation.eventChain.length > 0) {
    rationale += `\nDecision Timeline:\n`;
    explanation.eventChain.forEach((event, idx) => {
      const time = new Date(event.timestamp).toISOString();
      rationale += `  ${idx + 1}. [${time}] ${event.type}`;
      if (event.toolName) {
        rationale += ` - ${event.toolName}`;
      }
      rationale += `\n`;
    });
  }

  rationales[trace.itemId] = rationale;
  return rationales;
}

/**
 * Renders a snapshot delta (comparison between two snapshots).
 * 
 * @param oldView - Executive view of the old snapshot
 * @param newView - Executive view of the new snapshot
 * @param oldSnapshot - Old priority snapshot
 * @param newSnapshot - New priority snapshot
 * @returns Deterministic delta description
 */
export function renderSnapshotDelta(
  oldView: ExecutivePriorityView | null,
  newView: ExecutivePriorityView,
  oldSnapshot: PrioritySnapshot | null,
  newSnapshot: PrioritySnapshot
): string {
  if (!oldView || !oldSnapshot) {
    return `New priority snapshot created using ${newSnapshot.toolName}.\n`;
  }

  let delta = `Priority snapshot updated:\n\n`;

  // Compare ranks
  if (oldView.rank !== null && newView.rank !== null) {
    const rankChange = oldView.rank - newView.rank;
    if (rankChange > 0) {
      delta += `Rank improved: #${oldView.rank} → #${newView.rank} (moved up ${rankChange} position${rankChange !== 1 ? "s" : ""})\n`;
    } else if (rankChange < 0) {
      delta += `Rank changed: #${oldView.rank} → #${newView.rank} (moved down ${Math.abs(rankChange)} position${Math.abs(rankChange) !== 1 ? "s" : ""})\n`;
    } else {
      delta += `Rank unchanged: #${newView.rank}\n`;
    }
  }

  // Compare scores
  if (oldView.numericScore !== null && newView.numericScore !== null) {
    const scoreChange = newView.numericScore - oldView.numericScore;
    if (Math.abs(scoreChange) > 0.01) {
      delta += `Score changed: ${oldView.numericScore.toFixed(2)} → ${newView.numericScore.toFixed(2)} (${scoreChange > 0 ? "+" : ""}${scoreChange.toFixed(2)})\n`;
    } else {
      delta += `Score unchanged: ${newView.numericScore.toFixed(2)}\n`;
    }
  }

  // Compare tools
  if (oldSnapshot.toolName !== newSnapshot.toolName) {
    delta += `Tool changed: ${oldSnapshot.toolName} → ${newSnapshot.toolName}\n`;
  }

  return delta;
}


